import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Lock, ShieldCheck, Network, Ban, Landmark, Building2, HeartPulse, Siren, Cpu,
  Droplets, CreditCard, Activity, Truck, Router, Radio, Server, Cable, KeyRound, Layers, Laptop, Share2,
} from "lucide-react";
import SEO from "@/components/SEO";

const BENEFITS = [
  { icon: <ShieldCheck size={22} />, title: "Enhanced security", text: "Traffic is fully isolated from the public internet, closing off the internet-facing attack surface entirely." },
  { icon: <Network size={22} />, title: "Network control", text: "IP addresses and routing rules are managed internally, giving your team full control over how devices connect." },
  { icon: <Router size={22} />, title: "Custom routing", text: "Data routes directly into your corporate data centre or cloud environment — no public-internet hops in between." },
  { icon: <Ban size={22} />, title: "Malware protection", text: "Public-facing threats, scanning bots and cyberattacks are blocked before they ever reach your devices." },
];

const USE_CASES = [
  { icon: <Droplets size={20} />, title: "Critical infrastructure", text: "Securely connects remote water pumps, power grids and SCADA systems without exposing them to the public internet." },
  { icon: <CreditCard size={20} />, title: "Retail & POS", text: "Protects card transactions and EFTPOS terminals with a private, isolated data path for every payment." },
  { icon: <Activity size={20} />, title: "Healthcare IoT", text: "Keeps patient vitals and medical device data private, encrypted and off the open internet." },
  { icon: <Truck size={20} />, title: "Connected fleets", text: "Tracks high-value cargo and vehicles with continuous, private telemetry that can't be intercepted." },
];

const AUDIENCE = [
  { icon: <Landmark size={16} />, label: "Government & defence" },
  { icon: <Building2 size={16} />, label: "Financial services" },
  { icon: <HeartPulse size={16} />, label: "Healthcare" },
  { icon: <Siren size={16} />, label: "Law enforcement & first response" },
  { icon: <Network size={16} />, label: "Critical infrastructure & utilities" },
  { icon: <Cpu size={16} />, label: "High-security IoT fleets" },
];

const QUOTE_TIERS = [
  {
    id: "dynamic-apn",
    icon: <Layers size={22} />,
    title: "Dynamic IP Private APN",
    tag: "Custom pricing",
    text: "Devices draw a temporary IP from a private pool — the most cost-effective way to isolate large device fleets from the public internet.",
    points: ["Best for large-scale, phone-home device fleets", "Minimal IP management overhead", "Fully isolated from the public internet"],
  },
  {
    id: "static-apn",
    icon: <KeyRound size={22} />,
    title: "Static IP Private APN",
    tag: "Custom pricing",
    text: "Every SIM is assigned a permanent, fixed IP address — enabling true two-way communication with your servers at any time.",
    points: ["Servers can initiate contact with remote devices", "Ideal for remote management & push commands", "Built for SCADA polling & always-addressable hardware"],
    featured: true,
  },
  {
    id: "apn-vpn",
    icon: <Cable size={22} />,
    title: "Private APN + IPSec VPN",
    tag: "Custom pricing",
    text: "Layer an encrypted IPSec VPN tunnel over your Private APN (or standard infrastructure) for standards-based, cost-effective security.",
    points: ["Encrypts data in transit end-to-end", "Cost-effective for public-infrastructure links", "Combine with static or dynamic IP APN"],
  },
];

const PrivateAPN = () => (
  <div data-testid="private-apn-page">
    <SEO
      path="/solutions/private-apn"
      title="Private APN & Secure VPN — Custom Connectivity"
      description="Custom Private APN, static/dynamic IP routing and IPSec VPN from PTT NZ — secure gateways between your devices and network, away from the public internet."
      keywords="private APN New Zealand, private APN Australia, static IP APN SIM, dynamic IP APN, secure VPN mobile network business, dedicated APN business SIM NZ AU, isolated cellular network enterprise, secure mobile connectivity government NZ, IPSec VPN cellular, private network SIM cards NZ, custom connectivity solutions Australia"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Private APN & Secure VPN Connectivity",
        provider: { "@type": "Organization", name: "PTT NZ Limited" },
        areaServed: ["New Zealand", "Australia"],
        description: "Custom-built Private APN, static/dynamic IP routing and IPSec VPN connectivity solutions that bypass the public internet, for government, healthcare, finance, critical infrastructure and IoT deployments.",
      }}
    />

    {/* HERO */}
    <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-14">
        <div className="eyebrow">Custom connectivity</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 leading-[1.02] tracking-tight max-w-3xl">
          Private APN &amp; Secure VPN — <span className="text-[var(--ptt-teal-deep)]">connectivity the public internet never touches.</span>
        </h1>
        <p className="text-[var(--ptt-muted)] mt-5 max-w-2xl leading-relaxed">
          A Private Access Point Name (APN) gives your multi-network cellular devices a secure, direct gateway straight into your corporate network — bypassing the public internet completely. PTT NZ builds and prices these solutions around your exact deployment, from a handful of SIMs to fleet-wide national rollouts.
        </p>
        <div className="mt-7 flex gap-3 flex-wrap">
          <Link to="/contact?interest=Private%20APN%20%2F%20Secure%20VPN" data-testid="apn-hero-cta-contact" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">Get a custom quote <ArrowRight size={14} /></Link>
        </div>
      </div>
    </section>

    {/* DIAGRAM */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
      <div className="eyebrow">How it works</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">A direct, private path — no detour through the open internet.</h2>

      <div className="mt-10 border border-[var(--ptt-line)] bg-white p-4 lg:p-6" data-testid="apn-architecture-diagram">
        <img
          src="/assets/apn-architecture-diagram.webp"
          alt="Architecture diagram showing a device connecting through a secure private APN tunnel directly to a corporate network, bypassing the public internet"
          className="w-full h-auto"
        />
        <div className="grid sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-[var(--ptt-line)]">
          <div className="flex items-center gap-2.5">
            <Radio size={16} className="text-[var(--ptt-teal-deep)] shrink-0" />
            <div>
              <div className="font-display text-xs font-bold">Your device / SIM</div>
              <div className="text-[10px] text-[var(--ptt-muted)]">PTT radio, camera, router, sensor</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <Lock size={16} className="text-[var(--ptt-teal-deep)] shrink-0" />
            <div>
              <div className="font-display text-xs font-bold">Private APN / VPN tunnel</div>
              <div className="text-[10px] text-[var(--ptt-muted)]">Static or dynamic IP, IPSec optional</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <Server size={16} className="text-[var(--ptt-teal-deep)] shrink-0" />
            <div>
              <div className="font-display text-xs font-bold">Your network</div>
              <div className="text-[10px] text-[var(--ptt-muted)]">Data centre, cloud VPC or control room</div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2.5 mt-6 pt-6 border-t border-dashed border-[var(--ptt-line)]">
          <Ban size={15} className="text-[var(--ptt-muted)]" />
          <span className="font-mono-tech text-[11px] uppercase tracking-widest text-[var(--ptt-muted)]">Public internet — bypassed completely, end to end</span>
        </div>
      </div>
    </section>

    {/* DEVICE MESH + VPN DIAGRAM */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-t border-[var(--ptt-line)]">
      <div className="eyebrow">Beyond point-to-point</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">One private network — every device, plus secure remote access.</h2>
      <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl leading-relaxed">
        A Private APN isn&apos;t limited to a single tunnel back to base. Every device on the same APN can talk directly to every other device — ideal for M2M fleets, distributed sensors and site-to-site links — while your team taps into that same private network over a secure VPN for remote management or a direct server connection, all without a single packet touching the public internet.
      </p>

      <div className="mt-10 border border-[var(--ptt-line)] bg-white p-4 lg:p-6" data-testid="apn-mesh-diagram">
        <img
          src="/assets/apn-mesh-vpn-diagram.webp"
          alt="Diagram showing a group of cellular devices communicating with each other through a private APN hub, with a secure VPN connection tapping in remotely for direct server access"
          className="w-full h-auto"
        />
        <div className="grid sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-[var(--ptt-line)]">
          <div className="flex items-center gap-2.5">
            <Share2 size={16} className="text-[var(--ptt-teal-deep)] shrink-0" />
            <div>
              <div className="font-display text-xs font-bold">Devices talk to each other</div>
              <div className="text-[10px] text-[var(--ptt-muted)]">Direct device-to-device, no detour required</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <Laptop size={16} className="text-[var(--ptt-teal-deep)] shrink-0" />
            <div>
              <div className="font-display text-xs font-bold">Secure VPN remote access</div>
              <div className="text-[10px] text-[var(--ptt-muted)]">Tap into the network or a server directly</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <Ban size={16} className="text-[var(--ptt-teal-deep)] shrink-0" />
            <div>
              <div className="font-display text-xs font-bold">Public internet, bypassed</div>
              <div className="text-[10px] text-[var(--ptt-muted)]">The whole network stays private, end to end</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* BENEFITS */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-t border-[var(--ptt-line)]">
      <div className="eyebrow">Why go private</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Four reasons businesses move off the public internet.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="apn-benefits-grid">
        {BENEFITS.map((b) => (
          <div key={b.title} className="bg-white border border-[var(--ptt-line)] p-6 hover:border-[var(--ptt-ink)] transition-colors" data-testid={`apn-benefit-${b.title.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/15">{b.icon}</div>
            <h3 className="font-display text-lg font-bold mt-5">{b.title}</h3>
            <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{b.text}</p>
          </div>
        ))}
      </div>
    </section>

    {/* USE CASES */}
    <section className="bg-[var(--ptt-ink)] text-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <div className="eyebrow text-white/50">High-value use cases</div>
        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Where a private path matters most.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="apn-use-cases-grid">
          {USE_CASES.map((u) => (
            <div key={u.title} className="border border-white/15 p-6" data-testid={`apn-use-case-${u.title.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="w-10 h-10 grid place-items-center bg-white/10 text-[var(--ptt-teal)]">{u.icon}</div>
              <h3 className="font-display text-base font-bold mt-4">{u.title}</h3>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">{u.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CONFIGURATION / QUOTE TIERS */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-b border-[var(--ptt-line)]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <div className="eyebrow">Pricing &amp; configuration</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05]">No fixed catalogue — built entirely around your deployment.</h2>
        </div>
      </div>
      <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl leading-relaxed">
        We have extensive architecture options and pricing flexibility up our sleeve — enough to build a custom Private APN, VPN or hybrid connectivity solution for any business or private user, from a handful of SIMs to a nationwide fleet. The three starting points below are all fully customisable.
      </p>

      <div className="grid md:grid-cols-3 gap-5 mt-10" data-testid="apn-quote-tiers">
        {QUOTE_TIERS.map((t) => (
          <div
            key={t.id}
            data-testid={`apn-tier-${t.id}`}
            className={`relative flex flex-col p-6 border transition-colors ${t.featured ? "border-[var(--ptt-teal-deep)] bg-[var(--ptt-teal)]/[0.07]" : "border-[var(--ptt-line)] bg-white hover:border-[var(--ptt-ink)]"}`}
          >
            {t.featured && (
              <div className="absolute -top-3 left-4 bg-[var(--ptt-teal-deep)] text-white text-[10px] font-mono-tech uppercase tracking-widest px-2.5 py-1">Most requested</div>
            )}
            <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/15">{t.icon}</div>
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-teal-deep)] mt-4">{t.tag}</div>
            <h3 className="font-display text-lg font-bold mt-1.5">{t.title}</h3>
            <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{t.text}</p>
            <ul className="mt-4 space-y-2 text-sm">
              {t.points.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-[var(--ptt-ink-soft)]"><span className="text-[var(--ptt-teal-deep)] mt-1">•</span>{p}</li>
              ))}
            </ul>
            <Link
              to={`/contact?interest=${encodeURIComponent("Private APN / Secure VPN")}&plan=${encodeURIComponent(t.title)}`}
              data-testid={`apn-tier-cta-${t.id}`}
              className="mt-5 pt-4 border-t border-[var(--ptt-line)] text-sm font-semibold inline-flex items-center gap-1.5 signal-underline"
            >
              Get a custom quote <ArrowRight size={13} />
            </Link>
          </div>
        ))}
      </div>

      <div className="mt-8 border border-[var(--ptt-line)] bg-[var(--ptt-paper)] p-7">
        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Built for organisations that can&apos;t compromise</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5" data-testid="apn-audience">
          {AUDIENCE.map((a) => (
            <div key={a.label} className="flex items-center gap-2.5 text-sm font-medium">
              <span className="text-[var(--ptt-teal-deep)]">{a.icon}</span>{a.label}
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* FINAL CTA */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 mb-8">
      <div className="relative border border-[var(--ptt-ink)] p-8 lg:p-14 bg-white overflow-hidden">
        <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[var(--ptt-teal)]/20 blur-3xl" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow">Talk to us</div>
            <h3 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Tell us what you need to isolate, and we&apos;ll price it.</h3>
            <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl">Static IP, dynamic IP, IPSec VPN, or a hybrid of all three — every Private APN build is engineered and priced around your compliance, security and scale requirements.</p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Link to="/contact?interest=Private%20APN%20%2F%20Secure%20VPN" data-testid="apn-final-cta-contact" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Get a custom quote <ArrowRight size={16} /></Link>
            <Link to="/solutions" data-testid="apn-final-cta-solutions" className="ghost-button px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Back to Solutions</Link>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default PrivateAPN;
